import type { Device, DeviceFormValues, FuseKind, RisoSign } from '../types/hook-types';

// Migration maps for backwards compatibility with old enum values in the database
const FUSE_KIND_MIGRATION: Record<string, FuseKind> = {
    'DPatroon': 'D-patroon',
    'D-patroon': 'D-patroon',
    'Mespatroon': 'Mespatroon',
    'Automaat': 'Automaat',
    'Handmatig': '', // Old "Handmatig" value maps to empty string for new manual mode
};

const TRIPPING_CHAR_MIGRATION: Record<string, string> = {
    // D-patroon old values
    TraagGG: 'Traag gG',
    DiazedE16: 'DIAZED E16',
    DiazedSnel: 'DIAZED snel',
    DiazedFlink: 'DIAZED DII, DIII, DIV flink',
    DiazedGG: 'DIAZED DII, DIII, DIV gG',
    DPatronenGIGG: 'D-patronen gI/gG',
    // Mespatroon old values
    GGAC500V: 'gG AC 500V',
    KTFGG500VAC: 'KTF gG 500 VAC',
    AMAC500V: 'aM AC 500V',
    GIGGAC500V: 'gI/gG AC 500V',
    IsodinGGGF400V: 'Isodin gG/gF 400V',
    IsodinGGGT500V: 'Isodin gG/gT 500V',
    // Automaat old values
    B: 'B-karakteristiek',
    C: 'C-karakteristiek',
    D: 'D-karakteristiek',
    G: 'G-karakteristiek',
    K: 'K-karakteristiek',
    Z: 'Z-karakteristiek',
};

/**
 * Normalize FuseKind from old enum values to new Dutch literals.
 * Handles backwards compatibility with existing database records.
 */
export function normalizeFuseKind(value: string | null | undefined): FuseKind | null {
    if (!value)
        return null;
    return FUSE_KIND_MIGRATION[value] ?? (value as FuseKind);
}

/**
 * Normalize TrippingCharacteristic from old enum values to new Dutch literals.
 * Handles backwards compatibility with existing database records.
 */
export function normalizeTrippingCharacteristic(value: string | null | undefined): string | null {
    if (!value)
        return null;
    return TRIPPING_CHAR_MIGRATION[value] ?? value;
}

/**
 * Map a Device API object to DeviceFormValues for form state.
 * Applies normalization for backwards compatibility with old enum values.
 */
export function mapDeviceToFormValues(device: Device): DeviceFormValues {
    return {
        id: device.ElectricalProjectDistributionBoardDeviceId ?? '',
        identifier: device.Identifier ?? '',
        residualCurrentDeviceType: device.ResidualCurrentDeviceType ?? null,
        ratedCurrent: device.RatedCurrent ?? null,
        tripDelay: device.TripDelay ?? null,
        isTestButtonFunctional: device.IsTestButtonFunctional ?? null,
        fuseKind: normalizeFuseKind(device.FuseKind),
        trippingCharacteristic: normalizeTrippingCharacteristic(device.TrippingCharacteristic),
        breakerRatedCurrent: device.BreakerRatedCurrent ?? null,
        powerPhaseKind: device.PowerPhaseKind ?? null,
        risoSign: (device.RisoSign as RisoSign) ?? null,
        risoValue: device.RisoValue ?? null,
        // Impedance loop fields
        ziResistance: device.ZiResistance ?? null,
        ziCurrent: device.ZiCurrent ?? null,
        ziMinimalCurrent: device.ZiMinimalCurrent ?? null,
        zsResistance: device.ZsResistance ?? null,
        zsCurrent: device.ZsCurrent ?? null,
        zsMinimalCurrent: device.ZsMinimalCurrent ?? null,
    };
}

/**
 * Map DeviceFormValues back to API format for patching.
 * Returns only the changed fields for partial updates.
 */
export function mapFormValuesToDevice(formValues: Partial<DeviceFormValues>): Record<string, unknown> {
    const result: Record<string, unknown> = {};

    if (formValues.identifier !== undefined) {
        result.Identifier = formValues.identifier;
    }
    if (formValues.residualCurrentDeviceType !== undefined) {
        result.ResidualCurrentDeviceType = formValues.residualCurrentDeviceType;
    }
    if (formValues.ratedCurrent !== undefined) {
        result.RatedCurrent = formValues.ratedCurrent;
    }
    if (formValues.tripDelay !== undefined) {
        result.TripDelay = formValues.tripDelay;
    }
    if (formValues.isTestButtonFunctional !== undefined) {
        result.IsTestButtonFunctional = formValues.isTestButtonFunctional;
    }
    if (formValues.fuseKind !== undefined) {
        result.FuseKind = formValues.fuseKind;
    }
    if (formValues.trippingCharacteristic !== undefined) {
        result.TrippingCharacteristic = formValues.trippingCharacteristic;
    }
    if (formValues.breakerRatedCurrent !== undefined) {
        result.BreakerRatedCurrent = formValues.breakerRatedCurrent;
    }
    if (formValues.powerPhaseKind !== undefined) {
        result.PowerPhaseKind = formValues.powerPhaseKind;
    }
    if (formValues.risoSign !== undefined) {
        result.RisoSign = formValues.risoSign;
    }
    if (formValues.risoValue !== undefined) {
        result.RisoValue = formValues.risoValue;
    }
    // Impedance loop fields
    if (formValues.ziResistance !== undefined) {
        result.ZiResistance = formValues.ziResistance;
    }
    if (formValues.ziCurrent !== undefined) {
        result.ZiCurrent = formValues.ziCurrent;
    }
    if (formValues.ziMinimalCurrent !== undefined) {
        result.ZiMinimalCurrent = formValues.ziMinimalCurrent;
    }
    if (formValues.zsResistance !== undefined) {
        result.ZsResistance = formValues.zsResistance;
    }
    if (formValues.zsCurrent !== undefined) {
        result.ZsCurrent = formValues.zsCurrent;
    }
    if (formValues.zsMinimalCurrent !== undefined) {
        result.ZsMinimalCurrent = formValues.zsMinimalCurrent;
    }

    return result;
}
